import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { DynamicForm } from '../../DynamicForm';
import { withStyles, Button, CircularProgress } from '@material-ui/core';
import cn from 'classnames';
import { useDropzone } from 'react-dropzone';
import FileIcon from '@material-ui/icons/Description';
import DeleteIcon from '@material-ui/icons/Delete';

// SERVICES
import {
  getOrderAttachments,
  getReportAttachments,
  removeOrderAttachment,
  uploadOrderAttachment,
} from '../../../services';
import classNames from 'classnames';

const styles = theme => ({
  container: {
    flex: 1,
    backgroundColor: 'white',
    padding: 20,
    '& > *': {
      alignSelf: 'stretch',
    },

    '& h2': {
      textAlign: 'left',
    },
  },
  dropzone: {
    backgroundColor: '#F8F8F8',
    borderStyle: 'dashed',
    borderWidth: 2,
    borderColor: 'rgba(0,0,0,0.1)',
    color: 'rgba(0,0,0,0.4)',
    padding: 20,
    textAlign: 'center',
  },
  dialogTitle: {
    backgroundColor: '#fafafa',
    borderBottom: '1px solid #e1e1e1',
  },
  dialogActions: {
    backgroundColor: '#fafafa',
    borderBottom: '1px solid #e1e1e1',
  },
  cancelButton: {
    color: '#f44336',
  },
  removeButton: {
    color: '#f44336',
  },
  circularProgress: {
    padding: '6px 8px',
  },
  orderAttachmentList: {
    paddingLeft: 0,
    listStyle: 'none',
  },
  attachmentListItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  fileLink: {
    display: 'flex',
    flexDirection: 'row',
    color: 'var(--jmi-blue)',
  },
  uploadButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    marginTop: 15,
  },
  errorMessage: { color: '#c33' },

  documentNameInput: {
    background: 'transparent',
    fontSize: 16,

    border: '1px solid #aaa',
    borderRadius: 5,
    padding: 10,
    width: 'calc(100% - 40px)', // padding * 4 to offset overlap
    flex: 1,

    '&:focus': {
      outline: 'none',
    },
  },

  uploadTable: {
    width: '100%',

    '& th': {
      // textAlign: 'left',
      height: 40,
    },

    '& tbody > tr': {
      height: 50,
    },

    '& *': {
      textAlign: 'left',
    },
  },

  documentNameTd: {},

  localFilenameTd: {},

  deleteIcon: {
    color: 'var(--jmi-red)',
    borderRadius: '50%',
    padding: 5,
    cursor: 'pointer',
    marginRight: 10,

    '&:hover': {
      background: 'rgba(0,0,0,0.1)',
    },

    '&:active': {
      background: 'rgba(0,0,0,0.2)',
    },
  },
  deleteIconDisabled: {
    color: 'gray',
    opacity: 0.3,
    cursor: 'not-allowed',

    '&:hover': {
      backgroundColor: 'transparent',
    },
  },

  existingFiles: {
    marginTop: 25,
  },
});

export const OrderDetailAttachedDocsTab = withStyles(styles)(({ classes, orderLineId }) => {
  const [files, setFiles] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [orderAttachments, setOrderAttachments] = useState([]);
  const [reportAttachments, setReportAttachments] = useState([]);

  const getExistingAdminAttachments = async () => {
    try {
      const existingOrderAttachments = await getOrderAttachments(orderLineId);
      setOrderAttachments(existingOrderAttachments);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const getExistingReportAttachments = async () => {
    try {
      const existingReportAttachments = await getReportAttachments(orderLineId);
      setReportAttachments(existingReportAttachments);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: '.pdf,.jpg,.jpeg,.png,.doc,.docx',
    onDrop: acceptedFiles => {
      setFiles(
        acceptedFiles.map(file => ({
          name: '',
          data: Object.assign(file, {
            preview: URL.createObjectURL(file),
          }),
        })),
      );
    },
  });

  const submitFiles = async () => {
    setSubmitting(true);
    setLoading(true);

    const uploadFiles = files.map(file => uploadFile(file));
    const newOrderAttachments = await Promise.all(uploadFiles);
    setOrderAttachments([...orderAttachments, ...newOrderAttachments]);
    setFiles([]);
    setSubmitting(false);
    setLoading(false);
  };

  const uploadFile = async file => {
    try {
      const orderAttachment = await uploadOrderAttachment(file, orderLineId);
      return orderAttachment;
    } catch (error) {
      const errorMessage = error.message
        ? `${file.path}: ${error.message}`
        : `${file.path}: Failed to upload file. Please check your connection and try again.`;
      setErrors([...errors, errorMessage]);
    }
  };

  // remove attached doc
  const removeOrderAttachmentAsync = async orderAttachmentId => {
    setLoading(true);
    try {
      const orderAttachment = await removeOrderAttachment(orderAttachmentId);
      const updateOrderAttachments = orderAttachments.filter(oa => oa.orderAttachmentId !== orderAttachmentId);
      setOrderAttachments(updateOrderAttachments);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      const errorMessage = error.message
        ? error.message
        : 'Failed to remove file. Please check your connection and try again.';
      setErrors([...errors, errorMessage]);
    }
  };

  // remove local file from list
  const removeFile = fileIndex => evt => {
    setFiles(files.filter((_, fIdx) => fIdx !== fileIndex));
  };

  const handleUploadCancel = () => {
    setFiles([]);
  };

  const updateDocumentName = fileIndex => evt => {
    const { value } = evt.target;

    const updateFiles = files.slice();

    updateFiles[fileIndex].name = value;
    setFiles(updateFiles);
  };

  const renderAttachment = (attachment, attachmentIndex, { fields, removeAttachment, removable }) => (
    <li key={attachment[fields.id]} className={classes.attachmentListItem}>
      <DeleteIcon
        //

        className={cn(classes.deleteIcon, !removable && classes.deleteIconDisabled)}
        onClick={removable ? () => removeAttachment(attachment[fields.id]) : null}
      />

      <a href={attachment[fields.location]} target="_blank" rel="noopener noreferrer" className={classes.fileLink}>
        <FileIcon />
        {attachment[fields.name]}
      </a>
    </li>
  );

  const renderOrderAttachment = (orderAttachment, orderAttachmentIndex) => {
    return renderAttachment(orderAttachment, orderAttachmentIndex, {
      fields: {
        id: 'orderAttachmentId',
        location: 'location',
        name: 'name',
      },
      removeAttachment: removeOrderAttachmentAsync,
      removable: true,
    });
  };

  const renderReportAttachment = (reportAttachment, reportAttachmentIndex) => {
    return renderAttachment(reportAttachment, reportAttachmentIndex, {
      fields: {
        id: 'reportAttachmentId',
        location: 'location',
        name: 'name',
      },
      removeAttachment: removeOrderAttachmentAsync,
      removable: false,
    });
  };

  useEffect(() => {
    getExistingAdminAttachments();
    getExistingReportAttachments();
  }, []);

  return (
    <React.Fragment>
      <main className={cn('col-start-center', classes.container)}>
        <section style={{ marginBottom: 20 }}>
          Upload documents to attach them to an order. You can upload PDFs, JPG and PNG images, and Word documents. If
          you do not type in a name for the document, it will default to the filename on your computer .
        </section>

        <div {...getRootProps({ className: classes.dropzone })}>
          <input {...getInputProps()} />
          <p>Drag PDF, JPG, PNG, & DOC files here or click to select files</p>
        </div>

        <h2>Files to Upload</h2>
        {!files.length && (
          <div style={{ textAlign: 'left' }} className={cn('text-neutral')}>
            Please add some files using the dropzone above
          </div>
        )}
        {!!files.length && (
          <React.Fragment>
            <table
              className={classes.uploadTable}
              style={{
                pointerEvents: submitting ? 'none' : 'auto',
              }}
            >
              <colgroup>
                <col span="1" style={{ width: '5%' }} />
                <col span="1" style={{ width: '30%' }} />
                <col span="1" style={{ width: '30%' }} />
                <col span="1" style={{ width: '35%' }} />
              </colgroup>

              <thead>
                <tr>
                  <th /> {/* delete icon */}
                  <th>Document Name</th>
                  <th>Local File Name</th>
                </tr>
              </thead>
              <tbody>
                {files.map((file, fileIndex) => (
                  <tr>
                    <td>
                      <DeleteIcon className={classes.deleteIcon} onClick={removeFile(fileIndex)} />
                    </td>
                    <td className={classes.documentNameTd}>
                      <input
                        className={cn(classes.documentNameInput)}
                        placeholder={'e.g. "Declaration for ..."'}
                        // placeholder="Defaults to local file name"
                        onChange={updateDocumentName(fileIndex)}
                        value={file.name}
                      />
                    </td>

                    <td className={classes.localFilenameTd}>{`${file.data.path}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className={classes.uploadButtonContainer}>
              <Button onClick={handleUploadCancel} disabled={submitting || loading} className={classes.cancelButton}>
                Cancel
              </Button>
              <Button color="primary" disabled={submitting || loading} onClick={submitFiles}>
                {submitting ? (
                  <CircularProgress color="primary" size={18} className={classes.circularProgress} />
                ) : (
                  'Upload'
                )}
              </Button>
            </div>
          </React.Fragment>
        )}

        <section className={classes.existingFiles}>
          <h2 style={{ marginBottom: 0 }}>Admin Attachments</h2>
          <div style={{ textAlign: 'left', color: 'rgba(0,0,0,0.5)' }}>
            Visible to all BlueSky users (not sent to client)
          </div>

          {loading ? (
            <CircularProgress color="primary" size={18} className={classes.circularProgress} />
          ) : !!orderAttachments.length ? (
            <ul className={classes.orderAttachmentList}>{orderAttachments.map(renderOrderAttachment)}</ul>
          ) : (
            <p>No order attachments</p>
          )}

          <div style={{ height: 2, background: 'rgba(0,0,0,0.1)', borderRadius: 2, margin: '50px 0' }} />

          <h2 style={{ marginBottom: 0 }}>Report Attachments</h2>
          <div style={{ textAlign: 'left', color: 'rgba(0,0,0,0.5)' }}>
            Uploaded via QA portal (attached to client PDF output)
          </div>

          {loading ? (
            <CircularProgress color="primary" size={18} className={classes.circularProgress} />
          ) : !!reportAttachments.length ? (
            <ul className={classes.orderAttachmentList}>{reportAttachments.map(renderReportAttachment)}</ul>
          ) : (
            <p>No report attachments</p>
          )}
        </section>
        <section>{!!errors.length && errors.map(error => <p className={classes.errorMessage}>{error}</p>)}</section>
      </main>
    </React.Fragment>
  );
});
OrderDetailAttachedDocsTab.propTypes = {};

OrderDetailAttachedDocsTab.defaultProps = {};
